import { GetdemoEventEmitter } from './getdemoEvents';
import { normalizeHostInitial, type HostInitialIntent } from '../bot/helpers/hostInitialMessage';

/**
 * `Getdemo.open()` / `Getdemo.close()` — o acionamento do chat pela plataforma do cliente.
 *
 * Pedido da Getrak (02/10/2026): um botão no ERP abre o chat, e fechar faz desaparecer até o
 * próximo clique. O estado armado vive no BotWidgetManager; aqui mora só a POLÍTICA — o que
 * acontece quando o cliente chama antes de existir agente para abrir.
 *
 *   open()
 *     │
 *     ├─ moldura montada ─────────────▶ revela ──▶ emite 'opened'
 *     │
 *     └─ nada montado ainda ─────────▶ INTENÇÃO PENDENTE
 *            │                              │
 *            │  resolução terminou e montou │  15s sem montar
 *            ▼                              ▼
 *        revela + 'opened'            emite 'unavailable' (uma vez)
 *
 * A intenção pendente existe por causa de um caso real: a Getrak tem audiência custom e chama
 * `Getdemo.identify` ~5s depois do load (pós-login). Nada é elegível no load, então um clique a
 * 2s não acharia moldura nenhuma. Sem a fila, o botão do cliente simplesmente não fazia nada —
 * que é exatamente o sintoma da regressão b738eb5b ("o Copilot sumiu no Getrak"), a falha mais
 * cara que este código já produziu. O timeout é 15s porque o identify medido chegou em 5,3s.
 */

export type ChatTriggerState = 'opened' | 'closed' | 'unavailable';

export const PENDING_TIMEOUT_MS = 15000;

export interface ChatTriggerDeps {
  /** A moldura do player está montada na página? */
  isMounted: () => boolean;
  /** Revela a moldura e abre o chat. Retorna false quando não havia moldura. */
  reveal: (intent?: HostInitialIntent) => boolean;
  /** Fecha o chat. Retorna false quando não havia nada aberto. */
  close: () => boolean;
  emitter: GetdemoEventEmitter;
  debug?: () => boolean;
}

export interface ChatTrigger {
  open: (intent?: HostInitialIntent) => void;
  close: () => void;
  /** O loader chama a cada vez que termina de resolver quem é elegível nesta página. */
  notifyResolved: () => void;
  /** O manager chama quando o visitante fechou o chat pelo X. */
  notifyClosed: () => void;
  hasPendingIntent: () => boolean;
}

const log = (deps: ChatTriggerDeps, message: string): void => {
  try {
    if (deps.debug?.()) console.log(`[Getdemo] ${message}`);
  } catch {
    /* console indisponível */
  }
};

export const createChatTrigger = (deps: ChatTriggerDeps): ChatTrigger => {
  let pending = false;
  let pendingIntent: HostInitialIntent | undefined;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearPending = (): void => {
    pending = false;
    pendingIntent = undefined;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const unavailable = (why: string): void => {
    clearPending();
    log(deps, `open() sem agente disponível (${why})`);
    deps.emitter.emit('chat', { state: 'unavailable' as ChatTriggerState });
  };

  const tryReveal = (intent?: HostInitialIntent): boolean => {
    if (!deps.isMounted()) return false;
    if (!deps.reveal(intent)) return false;
    clearPending();
    deps.emitter.emit('chat', { state: 'opened' as ChatTriggerState });
    return true;
  };

  return {
    open: raw => {
      const intent = normalizeHostInitial(raw);
      if (tryReveal(intent)) return;
      // Já havia uma intenção pendente: um segundo clique não reinicia o relógio nem enfileira
      // duas aberturas. A última chamada é a que vale — inclusive um open() sem argumento,
      // que deixa de pedir tarefa.
      if (pending) {
        pendingIntent = intent;
        return;
      }
      pending = true;
      pendingIntent = intent;
      log(deps, 'open() antes de haver agente montado — intenção pendente');
      timer = setTimeout(() => {
        timer = null;
        if (pending) unavailable('expirou aguardando elegibilidade');
      }, PENDING_TIMEOUT_MS);
    },

    close: () => {
      clearPending();
      // Só emite quando havia o que fechar: um close() no vazio é no-op silencioso.
      if (deps.close()) {
        deps.emitter.emit('chat', { state: 'closed' as ChatTriggerState });
      }
    },

    /**
     * Resolveu: se há intenção pendente e agora existe moldura, abre.
     *
     * Uma resolução que falha NÃO emite `unavailable` e NÃO descarta a intenção. O loader
     * re-resolve a cada mudança de URL (poll de 500ms), então a primeira falha emitiria
     * `unavailable` meio segundo depois do clique e jogaria fora justamente a espera pelo
     * identify tardio da Getrak. O único caminho para `unavailable` é o timeout.
     */
    notifyResolved: () => {
      if (!pending) return;
      tryReveal(pendingIntent);
    },

    notifyClosed: () => {
      deps.emitter.emit('chat', { state: 'closed' as ChatTriggerState });
    },

    hasPendingIntent: () => pending
  };
};

/**
 * Pendura `Getdemo.open`/`Getdemo.close` na superfície pública e DRENA o que o stub enfileirou
 * antes de o loader rodar. Espelha `ensureOnSurface`: idempotente, porque o SDK pode substituir
 * `window.Getdemo` durante a própria inicialização.
 */
export const ensureChatSurface = (trigger: ChatTrigger): void => {
  try {
    const w = window as any;
    const g = (w.Getdemo = w.Getdemo || { q: [] });

    if (Array.isArray(g.q)) {
      const remaining: any[] = [];
      let queuedOpen = false;
      let queuedClose = false;
      let queuedIntent: HostInitialIntent | undefined;
      for (const entry of g.q) {
        if (Array.isArray(entry) && (entry[0] === 'open' || entry[0] === 'close')) {
          if (entry[0] === 'open') {
            queuedOpen = true;
            queuedClose = false;
            // `entry[1]` é o objeto `arguments` do stub (`g.q.push([n, arguments])`): array-LIKE,
            // e `Array.isArray` é SEMPRE falso nele. Sem o slice o `{ task }` se perde.
            const args = entry[1] ? Array.prototype.slice.call(entry[1]) : [];
            queuedIntent = normalizeHostInitial(args[0]);
          } else {
            queuedClose = true;
            queuedOpen = false;
            queuedIntent = undefined;
          }
          continue;
        }
        remaining.push(entry);
      }
      g.q = remaining;
      // Um `close` enfileirado depois de um `open` ganha: a última intenção do visitante é a que
      // vale, e abrir para fechar no quadro seguinte piscaria o chat na cara dele.
      if (queuedClose) trigger.close();
      else if (queuedOpen) trigger.open(queuedIntent);
    }

    if (typeof g.open === 'function' && (g.open as any).__gdTriggerBound) return;

    const openBound = (intent?: HostInitialIntent) => trigger.open(intent);
    (openBound as any).__gdTriggerBound = true;
    g.open = openBound;

    const closeBound = () => trigger.close();
    (closeBound as any).__gdTriggerBound = true;
    g.close = closeBound;
  } catch {
    /* window indisponível */
  }
};
